// Service Gantt: every vessel's works on a timeline, from each work's Start to its Finish (the Service
// sheet's work-level date columns). Better than the assembly week grid in the ways the floor asked for:
//   - any span at three zooms (Weeks, Months, Quarter), scrolled to today, with a Today jump
//   - drag a bar to move a work, drag its ends to change Start or Finish (mouse); tap a bar to edit its dates
//     (touch); ← / → move a focused bar a day, Shift + ← / → stretch or shrink it. One write per change,
//     Start and Finish together, shown at once.
//   - colour by status (the sheet's RYG ball, with its word in the tooltip) or by type of work (a fixed
//     colour per type, from the column's own option order, so a type keeps its colour whatever is filtered)
//   - late works (Finish passed, not done) flagged in words, done works ticked and quiet
//   - works without dates listed under their vessel, one click from being scheduled
//   - each vessel a summary bar over its works, collapsible, with its done / late counts
// It is a view on the same filtered vessels as Board and Sheet, so search, group, season and status apply.
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';
import { createPortal } from 'react-dom';
import type { SheetGridRow } from '../features/hooks.js';
import { Modal, Segmented } from '../components/ui.js';
import { IconCheck, IconChevronRight } from '../components/icons.js';
import { useDateFormatter } from '../lib/format.js';
import { localTodayIso } from '../lib/dates.js';
import { useIsCoarse, useIsPhone } from '../lib/media.js';
import { useTabState } from '../lib/tabState.js';
import { serviceStatusText } from '../features/serviceStatus.js';
import '../styles/service-gantt.css';

export type GanttTone = 'ok' | 'warn' | 'err' | 'info' | 'none';
export interface GanttWork {
  row: SheetGridRow;
  name: string;
  depth: number;
  /** 'YYYY-MM-DD' or '' */
  start: string;
  finish: string;
  done: boolean;
  tone: GanttTone | null;
  /** the status by its name (N/A – Not Applicable, In Progress, Completed, N/T – Not Tested) */
  status: string;
  type: string;
  assignee: string;
}
export interface GanttVessel {
  row: SheetGridRow;
  name: string;
  /** SAP · group, under the name */
  sub: string;
  works: GanttWork[];
}

const DAY = 86_400_000;
const ZOOMS = { week: { px: 34, label: 'Weeks' }, month: { px: 13, label: 'Months' }, quarter: { px: 5, label: 'Quarter' } } as const;
type Zoom = keyof typeof ZOOMS;
type ColourBy = 'status' | 'type';
/** the categorical slots (service-gantt.css --sg-c1…8); a 9th type and beyond share "Other" */
const SLOTS = 8;

const dayOf = (iso: string): number | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? Math.round(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!) / DAY) : null;
};
const isoOf = (day: number): string => new Date(day * DAY).toISOString().slice(0, 10);
/** 0 = Monday … 6 = Sunday */
const weekdayOf = (day: number) => (new Date(day * DAY).getUTCDay() + 6) % 7;

/** a work's span in whole days (inclusive), or null when it has no dates. A missing end takes the other. */
export function spanOf(w: { start: string; finish: string }): { a: number; b: number } | null {
  const s = dayOf(w.start);
  const f = dayOf(w.finish);
  if (s == null && f == null) return null;
  const a = s ?? f!;
  const b = f ?? s!;
  return a <= b ? { a, b } : { a: b, b: a };
}

/** the days the chart covers: every dated work and today, padded, starting on a Monday (Weeks) or the
 *  1st of a month (Months / Quarter), at least 10 weeks. */
export function rangeOf(spans: Array<{ a: number; b: number }>, today: number, zoom: Zoom): { from: number; to: number } {
  let lo = today - 14;
  let hi = today + 42;
  for (const s of spans) { lo = Math.min(lo, s.a - 7); hi = Math.max(hi, s.b + 21); }
  let from: number;
  if (zoom === 'week') from = lo - weekdayOf(lo);
  else { const d = new Date(lo * DAY); from = Math.round(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1) / DAY); }
  const to = Math.max(hi, from + 70);
  return { from, to };
}

interface Drag { rowId: number; mode: 'move' | 'start' | 'end'; x0: number; a0: number; b0: number; a: number; b: number; moved: boolean }

export function ServiceGantt({ vessels, typeOrder, hasStatus, hasType, singleDate, canWrite, onSetDates, onShowInBoard }: {
  vessels: GanttVessel[];
  /** every type of work in a fixed order (the column's options, then any others): colour slot = position */
  typeOrder: string[];
  hasStatus: boolean;
  hasType: boolean;
  /** the sheet has one date column only: a bar is a single day, and moving is all there is */
  singleDate: boolean;
  canWrite: boolean;
  onSetDates: (row: SheetGridRow, start: string | null, finish: string | null) => void;
  onShowInBoard: (vessel: SheetGridRow) => void;
}) {
  const df = useDateFormatter();
  const coarse = useIsCoarse();
  const isPhone = useIsPhone();
  const [zoom, setZoom] = useTabState<Zoom>('service.gantt.zoom', isPhone ? 'month' : 'week');
  const [colourBy, setColourBy] = useTabState<ColourBy>('service.gantt.colour', hasStatus ? 'status' : 'type');
  const [hideDone, setHideDone] = useTabState('service.gantt.hideDone', false);
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [drag, setDrag] = useState<Drag | null>(null);
  const [nudge, setNudge] = useState<{ rowId: number; a: number; b: number } | null>(null); // keyboard moves, saved after a pause
  const [editing, setEditing] = useState<{ work: GanttWork; vessel: GanttVessel } | null>(null);
  const [tip, setTip] = useState<{ work: GanttWork; x: number; y: number } | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const nudgeTimer = useRef<ReturnType<typeof setTimeout>>();
  // a mouse press that the drag handlers already dealt with (a drag, or a click that opened the editor), so
  // the click that follows it does nothing more; any other click (touch, keyboard, read-only) opens the editor
  const pressHandled = useRef(false);

  const today = dayOf(localTodayIso())!;
  const px = ZOOMS[zoom].px;
  const labelW = isPhone ? 136 : 290;
  const colourOf = colourBy === 'type' && hasType ? 'type' : hasStatus ? 'status' : 'type';

  const shown = useMemo(
    () => vessels.map((v) => ({ ...v, works: hideDone ? v.works.filter((w) => !w.done) : v.works })).filter((v) => v.works.length || !hideDone),
    [vessels, hideDone],
  );
  const allSpans = useMemo(() => shown.flatMap((v) => v.works.flatMap((w) => { const s = spanOf(w); return s ? [s] : []; })), [shown]);
  // the scroller's width, so a short range still fills it (no empty band after the last day)
  const [viewW, setViewW] = useState(0);
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setViewW(el.clientWidth));
    ro.observe(el);
    setViewW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const { from, to } = useMemo(() => {
    const r = rangeOf(allSpans, today, zoom);
    return { from: r.from, to: Math.max(r.to, r.from + Math.ceil(Math.max(0, viewW - labelW) / px)) };
  }, [allSpans, today, zoom, viewW, labelW, px]);
  const days = to - from + 1;
  const xOf = (day: number) => (day - from) * px;

  const counts = useMemo(() => {
    let scheduled = 0, unscheduled = 0, late = 0;
    for (const v of vessels) for (const w of v.works) {
      const s = spanOf(w);
      if (!s) unscheduled++;
      else { scheduled++; if (!w.done && s.b < today) late++; }
    }
    return { scheduled, unscheduled, late };
  }, [vessels, today]);

  // open on today (and again when the zoom changes), today a third of the way in
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    el.scrollLeft = Math.max(0, labelW + xOf(today) - (el.clientWidth - labelW) / 3 - labelW);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, from]);
  const jumpToday = () => {
    const el = scroller.current;
    if (el) el.scrollTo({ left: Math.max(0, xOf(today) - (el.clientWidth - labelW) / 3), behavior: 'smooth' });
  };

  // ── header scale ──────────────────────────────────────────────────────────
  const months = useMemo(() => {
    const out: Array<{ day: number; len: number; iso: string }> = [];
    let d = from;
    while (d <= to) {
      const dt = new Date(d * DAY);
      const next = Math.round(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth() + 1, 1) / DAY);
      out.push({ day: d, len: Math.min(next, to + 1) - d, iso: isoOf(d) });
      d = next;
    }
    return out;
  }, [from, to]);
  const ticks = useMemo(() => {
    const out: Array<{ day: number; len: number; label: string; today?: boolean; weekend?: boolean }> = [];
    if (zoom === 'week') {
      for (let d = from; d <= to; d++) out.push({ day: d, len: 1, label: String(new Date(d * DAY).getUTCDate()), today: d === today, weekend: weekdayOf(d) >= 5 });
    } else {
      // a tick per week, from the first Monday
      let d = from + ((7 - weekdayOf(from)) % 7);
      for (; d <= to; d += 7) out.push({ day: d, len: 7, label: zoom === 'month' ? String(new Date(d * DAY).getUTCDate()) : '' });
    }
    return out;
  }, [from, to, zoom, today]);

  // ── writes ────────────────────────────────────────────────────────────────
  const commit = (work: GanttWork, a: number, b: number) => {
    const s = spanOf(work);
    if (s && s.a === a && s.b === b) return;
    onSetDates(work.row, isoOf(a), singleDate ? null : isoOf(b));
  };
  const findWork = (rowId: number): GanttWork | undefined => {
    for (const v of vessels) for (const w of v.works) if (w.row.id === rowId) return w;
    return undefined;
  };

  // drag (mouse / pen): move the bar, or pull either end; whole days only
  const onBarDown = (e: PointerEvent<HTMLButtonElement>, w: GanttWork) => {
    if (!canWrite || e.pointerType === 'touch' || e.button !== 0) return;
    const s = spanOf(w);
    if (!s) return;
    const r = e.currentTarget.getBoundingClientRect();
    const off = e.clientX - r.left;
    const edge = Math.min(8, r.width / 3);
    const mode: Drag['mode'] = singleDate ? 'move' : off < edge ? 'start' : off > r.width - edge ? 'end' : 'move';
    e.currentTarget.setPointerCapture(e.pointerId);
    setTip(null);
    setDrag({ rowId: w.row.id, mode, x0: e.clientX, a0: s.a, b0: s.b, a: s.a, b: s.b, moved: false });
  };
  const onBarMove = (e: PointerEvent<HTMLButtonElement>) => {
    if (!drag) return;
    const dd = Math.round((e.clientX - drag.x0) / px);
    const moved = drag.moved || Math.abs(e.clientX - drag.x0) > 3;
    let { a, b } = drag;
    if (drag.mode === 'move') { a = drag.a0 + dd; b = drag.b0 + dd; }
    else if (drag.mode === 'start') a = Math.min(drag.a0 + dd, drag.b0);
    else b = Math.max(drag.b0 + dd, drag.a0);
    if (a !== drag.a || b !== drag.b || moved !== drag.moved) setDrag({ ...drag, a, b, moved });
  };
  const onBarUp = (e: PointerEvent<HTMLButtonElement>, w: GanttWork, v: GanttVessel) => {
    if (!drag) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    const d = drag;
    setDrag(null);
    pressHandled.current = true;
    if (!d.moved) { setEditing({ work: w, vessel: v }); return; }
    commit(w, d.a, d.b);
  };
  // Escape drops a drag in progress
  useEffect(() => {
    if (!drag) return;
    // (the release that follows is then not a click: it mustn't open the editor)
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') { pressHandled.current = true; setDrag(null); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drag]);

  // keyboard: ← / → move a day, Shift stretches the end; saved 700ms after the last press
  const onBarKey = (e: KeyboardEvent<HTMLButtonElement>, w: GanttWork) => {
    if (!canWrite || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
    const s = nudge?.rowId === w.row.id ? nudge : spanOf(w);
    if (!s) return;
    e.preventDefault();
    const step = e.key === 'ArrowLeft' ? -1 : 1;
    const next = e.shiftKey && !singleDate ? { a: s.a, b: Math.max(s.a, s.b + step) } : { a: s.a + step, b: s.b + step };
    setNudge({ rowId: w.row.id, ...next });
    clearTimeout(nudgeTimer.current);
    nudgeTimer.current = setTimeout(() => {
      const work = findWork(w.row.id);
      if (work) commit(work, next.a, next.b);
      setNudge(null);
    }, 700);
  };
  useEffect(() => () => clearTimeout(nudgeTimer.current), []);

  const liveSpan = (w: GanttWork) => {
    if (drag && drag.rowId === w.row.id) return { a: drag.a, b: drag.b };
    if (nudge && nudge.rowId === w.row.id) return { a: nudge.a, b: nudge.b };
    return spanOf(w);
  };

  // ── labels ────────────────────────────────────────────────────────────────
  const typeSlot = (t: string) => {
    const i = typeOrder.indexOf(t);
    return i >= 0 && i < SLOTS ? `c${i + 1}` : 'other';
  };
  const legend = colourOf === 'type'
    ? [...typeOrder.slice(0, SLOTS).map((t) => ({ key: typeSlot(t), label: t })), ...(typeOrder.length > SLOTS ? [{ key: 'other', label: 'Other' }] : [])]
    // the Status colours by their names (features/serviceStatus.ts); gray (N/T) and a blank share the plain bar
    : [
        { key: 'ok', label: 'Completed', title: serviceStatusText('Green') },
        { key: 'warn', label: 'In Progress', title: serviceStatusText('Yellow') },
        { key: 'err', label: 'N/A', title: serviceStatusText('Red') },
        { key: 'none', label: 'N/T or no status', title: serviceStatusText('Gray') },
      ];
  const rangeText = (a: number, b: number) => (a === b ? df.date(isoOf(a)) : df.range(isoOf(a), isoOf(b)));
  const daysText = (a: number, b: number) => `${b - a + 1} day${b === a ? '' : 's'}`;
  const describe = (w: GanttWork, s: { a: number; b: number } | null) => {
    const bits = [w.name || 'Work'];
    if (s) bits.push(rangeText(s.a, s.b), daysText(s.a, s.b)); else bits.push('not scheduled');
    if (w.status) bits.push(`status ${w.status}`);
    if (w.type) bits.push(w.type);
    if (w.done) bits.push('done');
    else if (s && s.b < today) bits.push('late');
    return bits.join(', ');
  };
  const toggleVessel = (id: number) => setCollapsed((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const style = {
    '--sg-day': `${px}px`,
    '--sg-label-w': `${labelW}px`,
    // the shading pattern repeats every 7 days with Saturday + Sunday at days 5–6, so it starts on a Monday
    '--sg-weekend-at': `${((7 - weekdayOf(from)) % 7) * px}px`,
    width: labelW + days * px,
  } as CSSProperties;

  if (!vessels.length) return null;

  return (
    <>
      <div className="sg-toolbar">
        <Segmented
          value={zoom}
          onChange={setZoom}
          ariaLabel="Zoom"
          options={(Object.keys(ZOOMS) as Zoom[]).map((z) => ({ value: z, label: ZOOMS[z].label }))}
        />
        <button type="button" className="btn btn-sm" onClick={jumpToday}>Today</button>
        {hasStatus && hasType && (
          <Segmented
            value={colourOf}
            onChange={(v) => setColourBy(v as ColourBy)}
            ariaLabel="Colour bars by"
            options={[{ value: 'status', label: isPhone ? 'Status' : 'By status' }, { value: 'type', label: isPhone ? 'Type' : 'By type' }]}
          />
        )}
        <label className="sg-check">
          <input type="checkbox" checked={hideDone} onChange={(e) => setHideDone(e.currentTarget.checked)} /> Hide done
        </label>
        <span className="sg-counts muted">
          {counts.scheduled} scheduled{counts.unscheduled ? ` · ${counts.unscheduled} without dates` : ''}
          {counts.late ? <> · <span className="sg-late-txt">{counts.late} late</span></> : null}
        </span>
      </div>
      <ul className="sg-legend" aria-label={colourOf === 'type' ? 'Colours: type of work' : 'Colours: status'}>
        {legend.map((l) => <li key={l.key} title={'title' in l ? l.title : undefined}><span className={`sg-swatch sg-${l.key}`} aria-hidden="true" />{l.label}</li>)}
        <li><span className="sg-swatch sg-swatch-late" aria-hidden="true" />Late</li>
        <li><span className="sg-swatch sg-swatch-done" aria-hidden="true"><IconCheck size={10} /></span>Done</li>
      </ul>

      <div className="tbl-wrap is-grid grid-fill sg-wrap" ref={scroller}>
        <div className={`sg sg-z-${zoom}${drag ? ' is-dragging' : ''}`} style={style} role="table" aria-label="Service works timeline">
          <div className="sg-head" role="row">
            <div className="sg-corner" role="columnheader">Vessel / work</div>
            <div className="sg-scale" role="columnheader" aria-label={`${df.date(isoOf(from))} to ${df.date(isoOf(to))}`}>
              <div className="sg-months">
                {months.map((m) => (
                  <span key={m.day} className="sg-month" style={{ left: xOf(m.day), width: m.len * px }}>
                    {m.len * px > 44 ? df.month(m.iso, { year: zoom !== 'quarter' || m.len * px > 70 }) : ''}
                  </span>
                ))}
              </div>
              <div className="sg-ticks">
                {ticks.map((t) => (
                  <span key={t.day} className={`sg-tick${t.today ? ' is-today' : ''}${t.weekend ? ' is-weekend' : ''}`} style={{ left: xOf(t.day), width: t.len * px }}>
                    {t.label}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="sg-body">
            <div className="sg-today" style={{ left: labelW + xOf(today) + px / 2 }} aria-hidden="true" />
            {shown.map((v) => {
              const spans = v.works.flatMap((w) => { const s = liveSpan(w); return s ? [s] : []; });
              const sum = spans.length ? { a: Math.min(...spans.map((s) => s.a)), b: Math.max(...spans.map((s) => s.b)) } : null;
              const done = v.works.filter((w) => w.done).length;
              const late = v.works.filter((w) => { const s = spanOf(w); return !w.done && s && s.b < today; }).length;
              const open = !collapsed.has(v.row.id);
              return (
                <div key={v.row.id} className="sg-group" role="rowgroup">
                  <div className="sg-row is-vessel" role="row">
                    <div className="sg-label" role="rowheader">
                      <button
                        type="button"
                        className={`sg-chev${open ? ' is-open' : ''}`}
                        aria-expanded={open}
                        aria-label={`${open ? 'Hide' : 'Show'} the works of ${v.name}`}
                        onClick={() => toggleVessel(v.row.id)}
                      ><IconChevronRight size={14} /></button>
                      <span className="sg-vname">
                        <button type="button" className="sg-vlink" onClick={() => onShowInBoard(v.row)} title="Show in Board">{v.name || '(unnamed)'}</button>
                        <span className="sg-vsub">
                          {v.sub && <>{v.sub} · </>}{done}/{v.works.length} done
                          {late > 0 && <> · <span className="sg-late-txt">{late} late</span></>}
                        </span>
                      </span>
                    </div>
                    <div className="sg-track" role="cell">
                      {sum && <span className="sg-sum" style={{ left: xOf(sum.a), width: (sum.b - sum.a + 1) * px }} aria-label={`${v.name}: ${rangeText(sum.a, sum.b)}`} />}
                    </div>
                  </div>
                  {open && v.works.map((w) => {
                    const s = liveSpan(w);
                    const isLate = !!s && !w.done && s.b < today;
                    const colour = colourOf === 'type' ? (w.type ? typeSlot(w.type) : 'none') : (w.tone ?? 'none');
                    const width = s ? (s.b - s.a + 1) * px : 0;
                    const live = drag?.rowId === w.row.id || nudge?.rowId === w.row.id;
                    return (
                      <div key={w.row.id} className={`sg-row${w.done ? ' is-done' : ''}`} role="row">
                        <div className="sg-label" role="rowheader" style={{ paddingLeft: 30 + w.depth * 14 }}>
                          {hasStatus && <span className={`sg-ball sg-${w.tone ?? 'none'}`} aria-hidden="true" />}
                          <span className="sg-wname" title={w.name}>{w.name || 'Work'}</span>
                        </div>
                        <div className="sg-track" role="cell">
                          {s ? (
                            <>
                              <button
                                type="button"
                                className={`sg-bar sg-${colour}${w.done ? ' is-done' : ''}${isLate ? ' is-late' : ''}${live ? ' is-live' : ''}${canWrite && !coarse ? ' can-drag' : ''}`}
                                style={{ left: xOf(s.a), width }}
                                aria-label={describe(w, s)}
                                aria-keyshortcuts={canWrite ? 'ArrowLeft ArrowRight Shift+ArrowLeft Shift+ArrowRight' : undefined}
                                onPointerDown={(e) => onBarDown(e, w)}
                                onPointerMove={onBarMove}
                                onPointerUp={(e) => onBarUp(e, w, v)}
                                onPointerCancel={() => setDrag(null)}
                                onClick={() => {
                                  if (pressHandled.current) { pressHandled.current = false; return; }
                                  setEditing({ work: w, vessel: v });
                                }}
                                onKeyDown={(e) => onBarKey(e, w)}
                                onPointerEnter={(e) => { if (e.pointerType !== 'touch' && !drag) setTip({ work: w, x: e.clientX, y: e.clientY }); }}
                                onPointerLeave={() => setTip(null)}
                              >
                                {w.done && <IconCheck size={11} />}
                              </button>
                              {/* the bar's words: what moves while dragging, else lateness or the type */}
                              <span className={`sg-note${isLate ? ' sg-late-txt' : ''}`} style={{ left: xOf(s.b + 1) + 6 }}>
                                {live ? `${rangeText(s.a, s.b)} · ${daysText(s.a, s.b)}`
                                  : isLate ? `Late · ${df.date(isoOf(s.b), { year: 'auto' })}`
                                  : colourOf === 'type' && w.type && zoom !== 'quarter' ? w.type : ''}
                              </span>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="sg-unsched"
                              style={{ left: xOf(today) }}
                              disabled={!canWrite}
                              onClick={() => setEditing({ work: w, vessel: v })}
                              aria-label={`${w.name || 'Work'}: not scheduled${canWrite ? '. Set its dates' : ''}`}
                            >{canWrite ? 'Not scheduled · set dates' : 'Not scheduled'}</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {tip && !drag && createPortal(
        <div className="sg-tip" style={{ left: Math.min(tip.x + 14, window.innerWidth - 270), top: tip.y + 16 }} role="tooltip">
          <b>{tip.work.name || 'Work'}</b>
          {(() => { const s = spanOf(tip.work); return s ? <span>{rangeText(s.a, s.b)} · {daysText(s.a, s.b)}</span> : <span>Not scheduled</span>; })()}
          {tip.work.status && <span><span className={`sg-ball sg-${tip.work.tone ?? 'none'}`} aria-hidden="true" /> {tip.work.status}</span>}
          {tip.work.type && <span>{tip.work.type}</span>}
          {tip.work.assignee && <span>{tip.work.assignee}</span>}
          {tip.work.done && <span><IconCheck size={11} /> Done</span>}
          {canWrite && <span className="sg-tip-hint">Drag to move, drag an end to change it, click to edit</span>}
        </div>,
        document.body,
      )}

      {editing && (
        <DatesEditor
          work={editing.work}
          vesselName={editing.vessel.name}
          singleDate={singleDate}
          canWrite={canWrite}
          onClose={() => setEditing(null)}
          onSave={(a, b) => { onSetDates(editing.work.row, a, singleDate ? null : b); setEditing(null); }}
          onShowInBoard={() => { onShowInBoard(editing.vessel.row); setEditing(null); }}
        />
      )}
    </>
  );
}

/** set or clear a work's Start and Finish (a tap on a bar, a click without a drag, or "set dates"). */
function DatesEditor({ work, vesselName, singleDate, canWrite, onClose, onSave, onShowInBoard }: {
  work: GanttWork;
  vesselName: string;
  singleDate: boolean;
  canWrite: boolean;
  onClose: () => void;
  onSave: (start: string | null, finish: string | null) => void;
  onShowInBoard: () => void;
}) {
  const today = localTodayIso();
  const [start, setStart] = useState(work.start || work.finish || today);
  const [finish, setFinish] = useState(work.finish || work.start || today);
  const bad = !singleDate && !!start && !!finish && finish < start;
  const n = !singleDate && start && finish && !bad ? (dayOf(finish)! - dayOf(start)! + 1) : null;
  return createPortal(
    <Modal
      title={work.name || 'Work'}
      dataTitle
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn" onClick={onShowInBoard}>Show in Board</button>
          {canWrite && (work.start || work.finish) && <button type="button" className="btn" onClick={() => onSave(null, null)}>Clear dates</button>}
          {canWrite && <button type="button" className="btn btn-primary" disabled={!start || bad} onClick={() => onSave(start || null, finish || null)}>Save</button>}
        </>
      )}
    >
      <p className="muted sg-ed-sub">{vesselName}</p>
      <div className="sg-ed-row">
        <label className="sg-ed-field">
          <span>{singleDate ? 'Date' : 'Start'}</span>
          <input className="field" type="date" value={start} disabled={!canWrite} onChange={(e) => setStart(e.currentTarget.value)} />
        </label>
        {!singleDate && (
          <label className="sg-ed-field">
            <span>Finish</span>
            <input className="field" type="date" value={finish} min={start || undefined} disabled={!canWrite} onChange={(e) => setFinish(e.currentTarget.value)} />
          </label>
        )}
      </div>
      {bad && <p className="sg-late-txt">Finish is before Start.</p>}
      {n != null && <p className="muted">{n} day{n === 1 ? '' : 's'}</p>}
    </Modal>,
    document.body,
  );
}
